import { App, Form, Input } from "antd"
import Col from "antd/es/grid/col"
import Row from "antd/es/grid/row"
import {
  type InternalUploadFile,
  type UploadChangeParam,
} from "antd/es/upload/interface"
import dayjs from "dayjs"
import { useEffect, useState } from "react"

import { classSessionAtoms, studentAtoms, tryoutAtoms } from "#atoms"
import CopyToClipboard from "#blocks/CopyToClipboard"
import Flex from "#blocks/Flex"
import CheckboxContentBlock from "#blocks/Form/CheckboxContentBlock"
import { SFormHeader } from "#blocks/Form/formComponents"
import Uploader from "#blocks/Uploader"
import Firebase from "#classes/Firebase"
import { Student } from "#classes/Student"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  UploadFile,
  UploadStatus,
} from "#customTypes/composedTypes/firestoreTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { GenericObject } from "#customTypes/types"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { normalizeEmailFormValue } from "#utils/emailHelpers"
import { ErrorType, formatError } from "#utils/textFormatters"

const STEP_1_TEXT = {
  studioTouring: {
    title: "Studio tour",
    description:
      "Make sure to ask lead teacher to do a tour for both student and parents.",
  },
  teacherHasEvalForm: {
    title: "Teacher has eval form",
    description:
      "Hand eval form to the teacher in class based on student's age group.",
  },
  emergencyContact: {
    title: "Emergency contact",
    description:
      "If not auto-filled, click the link icon to send the form for parent e-signature.",
  },
  releaseForm: {
    title: "Signed release form",
    description: "Parent has signed the consent/liability waiver form.",
  },
}

const TryoutProgressStep1 = () => {
  const form = Form.useFormInstance()
  const studentId = Form.useWatch("studentId", form)
  const tryoutId = Form.useWatch("id", form)

  // Watch guardian fields for auto-check
  const guardian1Name = Form.useWatch(["step1", "guardian1Name"], form)
  const guardian1Phone = Form.useWatch(["step1", "guardian1Phone"], form)
  const guardian1Email = Form.useWatch(["step1", "guardian1Email"], form)

  const { message } = App.useApp()
  const canSeeContactInfo = useHasPermission(
    RESOURCES.STUDENT,
    USER_ACTIONS.VIEW_CONTACT_INFO
  )

  // Track uploading file state to show pretty filename during upload
  const [uploadingFile, setUploadingFile] = useState<InternalUploadFile | null>(
    null
  )

  const tryout = useOneDoc<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    tryoutId
  )

  const student = useOneDoc(studentAtoms, DOC_TYPES.STUDENTS, studentId)

  const session = useOneDoc<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    tryout?.sessionId
  )
  const startDate = dayjs(session?.eventStart).format("M-D-YY")
  const releaseFormPrettyFilename = `Release Form - ${startDate}`
  const releaseFormFilename = "waiver"
  const releaseFormMetadata = {
    studentId,
    tryoutId,
    contentDisposition: `inline; filename="${releaseFormPrettyFilename}"`,
  }

  // Auto-check "Emergency contact" when any guardian info is filled
  useEffect(() => {
    if (guardian1Name || guardian1Phone || guardian1Email) {
      form.setFieldValue(["step1", "hasEmergencyContact"], true)
    }
  }, [guardian1Name, guardian1Phone, guardian1Email, form])

  // Get waiver from student.files.waiver, or use uploading file if in progress
  const waiverFile = student?.files?.waiver
  const releaseFormFile: InternalUploadFile[] = uploadingFile
    ? [uploadingFile]
    : waiverFile
      ? [
          {
            uid: "waiver",
            name: releaseFormPrettyFilename,
            status: waiverFile.status,
            url: waiverFile.url,
            percent: waiverFile.percent ?? 100,
          } as InternalUploadFile,
        ]
      : []

  // One batch, so the tryout's release-form flag and the student's waiver
  // file can't end up disagreeing when one of the two writes fails.
  const saveTryoutAndStudent = async (
    tryoutPatch: GenericObject,
    studentPatch: GenericObject
  ): Promise<boolean> => {
    try {
      await Firebase.getInstance().updateDocuments({
        [DOC_TYPES.TRYOUTS]: { [tryoutId]: tryoutPatch },
        [DOC_TYPES.STUDENTS]: { [studentId]: studentPatch },
      })
      return true
    } catch (error) {
      message.error(
        `Failed to save the release form: ${formatError(error as ErrorType)}`
      )
      return false
    }
  }

  const handleUploadStatusChange = (info: UploadChangeParam) => {
    const { status } = info.file

    if (status === UploadStatus.UPLOADING) {
      setUploadingFile({
        uid: info.file.uid,
        name: releaseFormPrettyFilename,
        status: UploadStatus.UPLOADING,
        percent: info.file.percent ?? 0,
      } as InternalUploadFile)
      return
    } else if (status === UploadStatus.DONE) {
      setUploadingFile(null)
      const result: UploadFile = {
        url: info.file.response,
        name: info.file.name,
        size: info.file.size,
        status: UploadStatus.DONE,
        uid: info.file.uid,
        percent: info.file.percent ?? 100,
      }

      form.setFieldValue(["step1", "hasSignedReleaseForm"], true)

      void saveTryoutAndStudent(
        {
          "step1.uploadedReleaseForm": result.url,
          "step1.hasSignedReleaseForm": true,
        },
        {
          "files.waiver.url": result.url,
          "files.waiver.name": releaseFormPrettyFilename,
          "files.waiver.size": result.size,
        }
      ).then((saved) => {
        if (saved) {
          message.success(`File ${info.file.name} uploaded successfully.`)
        }
      })
    } else if (status === UploadStatus.ERROR) {
      setUploadingFile(null)
      message.error(`File ${info.file.name} upload failed.`)
    }
  }

  const handleDeleteUploadedFile = () => {
    form.setFieldValue(["step1", "hasSignedReleaseForm"], false)

    void saveTryoutAndStudent(
      {
        "step1.uploadedReleaseForm": null,
        "step1.hasSignedReleaseForm": false,
      },
      { "files.waiver": null }
    )
  }

  return (
    <Flex col style={{ padding: "20px 30px", width: "100%" }} gap={30}>
      <Row gutter={48}>
        <Col
          span={12}
          style={{ display: "flex", flexDirection: "column", gap: 30 }}
        >
          <CheckboxContentBlock
            fieldName={["step1", "isTourCompleted"]}
            title={STEP_1_TEXT.studioTouring.title}
            description={STEP_1_TEXT.studioTouring.description}
          />
          <CheckboxContentBlock
            fieldName={["step1", "teacherHasEvalForm"]}
            title={STEP_1_TEXT.teacherHasEvalForm.title}
            description={STEP_1_TEXT.teacherHasEvalForm.description}
          />
        </Col>
        <Col
          span={12}
          style={{ display: "flex", flexDirection: "column", gap: 30 }}
        >
          <CheckboxContentBlock
            fieldName={["step1", "hasSignedReleaseForm"]}
            title={STEP_1_TEXT.releaseForm.title}
            description={STEP_1_TEXT.releaseForm.description}
          >
            <Uploader
              fileList={releaseFormFile}
              onStatusChange={handleUploadStatusChange}
              onDelete={handleDeleteUploadedFile}
              folder={`${studentId}/files`}
              filename={releaseFormFilename}
              metadata={releaseFormMetadata}
            />
          </CheckboxContentBlock>
        </Col>
      </Row>

      <Row gutter={30}>
        <Col span={24}>
          <CheckboxContentBlock
            fieldName={["step1", "hasEmergencyContact"]}
            title={STEP_1_TEXT.emergencyContact.title}
            description={
              <div>
                {STEP_1_TEXT.emergencyContact.description}
                {/* <Button
                  icon={<LinkOutlined />}
                  shape="circle"
                  type="text"
                  color="primary"
                  onClick={handleSendForm}
                /> */}
              </div>
            }
          >
            {canSeeContactInfo && (
              <>
                <Row gutter={24} style={{ marginBottom: 10, marginTop: 24 }}>
                  <Col span={10}>
                    <Form.Item
                      name={["step1", "guardian1Name"]}
                      label="Guardian 1 Name"
                      rules={
                        [
                          // { required: true, message: "Guardian's name is required" },
                        ]
                      }
                    >
                      <Input name="guardian1Name" />
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item
                      name={["step1", "guardian1Phone"]}
                      label="Guardian 1 Phone"
                      rules={
                        [
                          // { required: true, message: "Guardian's phone is required" },
                        ]
                      }
                    >
                      <Input name="guardian1Phone" />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name={["step1", "guardian1Email"]}
                      label="Guardian 1 Email"
                      normalize={normalizeEmailFormValue}
                      rules={
                        [
                          // { required: true, message: "Guardian's email is required" },
                        ]
                      }
                    >
                      <Input
                        name="guardian1Email"
                        type="email"
                        autoComplete="email"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={10}>
                    <Form.Item
                      name={["step1", "guardian2Name"]}
                      label="Guardian 2 Name"
                    >
                      <Input name="guardian2Name" />
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item
                      name={["step1", "guardian2Phone"]}
                      label="Guardian 2 Phone"
                    >
                      <Input name="guardian2Phone" />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name={["step1", "guardian2Email"]}
                      label="Guardian 2 Email"
                      normalize={normalizeEmailFormValue}
                    >
                      <Input
                        name="guardian2Email"
                        type="email"
                        autoComplete="email"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Flex col>
                    <SFormHeader>
                      <Flex style={{ alignItems: "center" }} gap={12}>
                        Primary Address{" "}
                        <CopyToClipboard
                          dataGetter={() =>
                            Student.formatAddress(
                              form.getFieldValue("primaryAddress")
                            )
                          }
                        />
                      </Flex>
                    </SFormHeader>

                    <Row gutter={16}>
                      <Col span={10}>
                        <Form.Item
                          name={["step1", "primaryAddress", "street1"]}
                          label="Street"
                        >
                          <Input placeholder="Enter street" />
                        </Form.Item>
                      </Col>

                      <Col span={10}>
                        <Form.Item
                          name={["step1", "primaryAddress", "street2"]}
                          label="Street 2"
                        >
                          <Input placeholder="Enter street 2" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={7}>
                        <Form.Item
                          name={["step1", "primaryAddress", "city"]}
                          label="City"
                        >
                          <Input placeholder="Enter city" />
                        </Form.Item>
                      </Col>

                      <Col span={6}>
                        <Form.Item
                          name={["step1", "primaryAddress", "state"]}
                          label="State"
                        >
                          <Input placeholder="Enter state" />
                        </Form.Item>
                      </Col>

                      <Col span={7}>
                        <Form.Item
                          name={["step1", "primaryAddress", "zipCode"]}
                          label="Zip code"
                        >
                          <Input placeholder="Enter zip code" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Flex>
                </Row>
              </>
            )}
          </CheckboxContentBlock>
        </Col>
      </Row>

      {/* Hidden field for uploaded release form */}
      <Form.Item
        name={["step1", "uploadedReleaseForm"]}
        style={{ display: "none" }}
      >
        <Input type="hidden" />
      </Form.Item>
    </Flex>
  )
}

export default TryoutProgressStep1
